/**
 * QuickTestsPage.tsx
 *
 * Quick Tests page for one-click access to common mobile network tests.
 * Features cluster/location selection, eSIM selection (a group, one specific
 * SIM, or a granted shared pool — see utils/esimSelection.ts), and the test tiles.
 */

import React, { useState, useEffect, useRef } from 'react';
import { Signal, MessageSquare, Database, Smartphone, Activity, Radio, Gauge, Zap, Phone, Bot } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { ResourceSelectors } from '@/components/quick-tests/ResourceSelectors';
import { QuickTestButton, QuickTestType, QUICK_TEST_ALLOCATION_CAPABILITIES } from '@/components/quick-tests/QuickTestButton';
import { someModemSupportsTest } from '@/utils/ratCapability';
import { ExecutionConfirmationDialog } from '@/components/quick-tests/ExecutionConfirmationDialog';
import { FlowInputsDialog, FlowInputDefinition } from '@/components/quick-tests/FlowInputsDialog';
import { TestLikeAMobileModal } from '@/components/test-like-mobile/TestLikeAMobileModal';
import { selectFirstAvailableResources } from '@/services/quickTestService';
import { getFlow } from '@/services/flowService';
import { getTestConfiguration, createTestRun } from '@/services/testConfigurationService';
import { executeFlow } from '@/services/runtime';
import { getCluster } from '@/services/clusterService';
import { getGroup, getProfile } from '@/services/esimService';
import { getLocations, filterLocationsByTenancy } from '@/services/hierarchicalLocationService';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { ESIM_MOVE_DECLINED, isEsimMoveApprovalError, useEsimMoveApproval } from '@/components/esim/EsimMoveApproval';
import { parseESimSelection, isConcreteProfileSelection } from '@/utils/esimSelection';
import type { Cluster, ESimGroup, ESimProfile, Location, ClusterItem, TestConfiguration, ForcedRat } from '@simcheck/shared-types';
import { isForcedRat, FORCED_RAT_LABELS, CAPABILITY_LABELS } from '@simcheck/shared-types';

/**
 * Resolve cluster items to location IDs
 * Handles region, country, and location level items
 */
const SELECTIONS_STORAGE_PREFIX = 'simcheck.quickTests.selections';

// The Step-3 APN used to be mandatory and pre-filled with `internet`, so
// nearly every browser has that value persisted without the user ever
// choosing it. Since issue #161 the field is an OVERRIDE (empty = use the
// eSIM's configured APN), so a persisted legacy `internet` is migrated to
// empty on restore — otherwise it would silently override every profile/
// group APN forever. Users who genuinely want to force `internet` can type
// it again.
const LEGACY_DEFAULT_APN = 'internet';

interface PersistedSelections {
  selectionMode: 'individual' | 'cluster';
  selectedClusterId: string | null;
  selectedLocationIds: string[];
  /** Encoded eSIM selection — group id, `profile:<id>`, or `shared:<sentinel>`. */
  selectedESimSelection: string | null;
  selectedNetworkMccMnc: string | null;
  selectedRat: ForcedRat;
  apn: string;
}

function storageKeyFor(orgId: string): string {
  return `${SELECTIONS_STORAGE_PREFIX}.${orgId}`;
}

function readPersistedSelections(orgId: string): PersistedSelections | null {
  try {
    const raw = localStorage.getItem(storageKeyFor(orgId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.selectionMode !== 'individual' && parsed?.selectionMode !== 'cluster') {
      return null;
    }
    return {
      selectionMode: parsed.selectionMode,
      selectedClusterId: parsed.selectedClusterId ?? null,
      selectedLocationIds: Array.isArray(parsed.selectedLocationIds) ? parsed.selectedLocationIds : [],
      // `selectedESimGroupId` is the pre-specific-SIM key name; still read it so
      // an existing browser keeps its selection across the rename.
      selectedESimSelection: parsed.selectedESimSelection ?? parsed.selectedESimGroupId ?? null,
      selectedNetworkMccMnc: parsed.selectedNetworkMccMnc ?? null,
      selectedRat: isForcedRat(parsed.selectedRat) ? parsed.selectedRat : 'auto',
      apn: (typeof parsed.apn === 'string' && parsed.apn.trim() && parsed.apn.trim() !== LEGACY_DEFAULT_APN)
        ? parsed.apn
        : '',
    };
  } catch {
    return null;
  }
}

function resolveClusterItems(items: ClusterItem[], allLocations: Location[]): string[] {
  const locationIds = new Set<string>();

  for (const item of items) {
    switch (item.type) {
      case 'location':
        // Direct location reference
        locationIds.add(item.id);
        break;
      case 'country':
        // Find all locations in this country
        allLocations
          .filter(loc => loc.countryId === item.id)
          .forEach(loc => locationIds.add(loc.id));
        break;
      case 'region':
        // Find all locations in this region
        allLocations
          .filter(loc => loc.regionId === item.id)
          .forEach(loc => locationIds.add(loc.id));
        break;
    }
  }

  return Array.from(locationIds);
}

export function QuickTestsPage() {
  const navigate = useNavigate();
  const { user, activeOrganizationId, isSuperAdmin } = useAuth();
  const { success: toastSuccess, error: toastError, info: toastInfo } = useToast();
  const { confirmMove, dialog: esimMoveDialog } = useEsimMoveApproval();
  const organizationId = activeOrganizationId;

  // Selection mode: 'individual' for specific locations, 'cluster' for cluster
  const [selectionMode, setSelectionMode] = useState<'individual' | 'cluster'>('individual');
  const [selectedClusterId, setSelectedClusterId] = useState<string | null>(null);
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);
  // Encoded eSIM selection: an org group id (allocator picks any member),
  // `profile:<id>` (one specific SIM), or `shared:<sentinel>` (shared pool).
  const [selectedESimSelection, setSelectedESimSelection] = useState<string | null>(null);
  const [selectedNetworkMccMnc, setSelectedNetworkMccMnc] = useState<string | null>(null);
  const [selectedNetworkLabel, setSelectedNetworkLabel] = useState<string | null>(null);
  const [selectedRat, setSelectedRat] = useState<ForcedRat>('auto');
  // Per-modem effective capabilities for the online modems at the selected
  // location(s), reported by ResourceSelectors. `null` = unknown (no location
  // selected / still loading) → don't gray out any test. Used to disable tests
  // that have no modem capable of both the forced RAT and the test's needs.
  const [modemCaps, setModemCaps] = useState<Array<Record<string, boolean>> | null>(null);
  // IMEI of the modem a pinned (shared/resident) SIM is locked to, or null when
  // the selection isn't pinned. When set, `modemCaps` is narrowed to just that
  // modem, and tests it can't run are grayed even on Automatic — a pinned SIM
  // has no other modem to fall back to.
  const [pinnedModemImei, setPinnedModemImei] = useState<string | null>(null);
  // Set when the chosen specific SIM can't serve the chosen location(s) (lives
  // on a modem elsewhere and can't be relocated, or a spent single-use code).
  // The backend would hard-fail such a run, so block launching it.
  const [esimBlockedReason, setEsimBlockedReason] = useState<string | null>(null);
  // APN override (Step 3, optional since issue #161). Empty = use the APN
  // configured on the eSIM (profile → group → network default); a typed
  // value overrides it for every test + TLAM in this wizard.
  const [apn, setApn] = useState<string>('');

  // Confirmation dialog state
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingTest, setPendingTest] = useState<{
    type: QuickTestType;
    label: string;
    description: string;
  } | null>(null);

  // Test Like A Mobile modal state
  const [showTestLikeAMobile, setShowTestLikeAMobile] = useState(false);
  const [testLikeAMobileAutoStart, setTestLikeAMobileAutoStart] = useState<{
    eSimProfileId: string;
    locationId: string;
    deviceId: string;
    modemImei: string;
    eSimGroupId?: string;
    forcedNetworkMccMnc?: string;
    forcedRat?: ForcedRat;
    apn?: string;
  } | undefined>(undefined);

  // Flow inputs dialog state
  const [showInputsDialog, setShowInputsDialog] = useState(false);
  const [pendingInputs, setPendingInputs] = useState<Record<string, any>>({});
  const [pendingFlowInputDefs, setPendingFlowInputDefs] = useState<FlowInputDefinition[]>([]);

  // Loading state for execution
  const [isExecuting, setIsExecuting] = useState(false);

  // Cached resource details for confirmation dialog
  const [selectedClusterDetails, setSelectedClusterDetails] = useState<Cluster | null>(null);
  const [selectedESimGroupDetails, setSelectedESimGroupDetails] = useState<ESimGroup | null>(null);
  const [selectedESimProfileDetails, setSelectedESimProfileDetails] = useState<ESimProfile | null>(null);
  const [allLocations, setAllLocations] = useState<Location[]>([]);

  // Tracks whether we've hydrated from localStorage for the current org — prevents
  // writing the default (empty) state back over a persisted value before restore runs.
  const hasRestoredRef = useRef<string | null>(null);

  // Restore persisted selections when the active org becomes known (or changes)
  useEffect(() => {
    if (!organizationId) return;
    if (hasRestoredRef.current === organizationId) return;

    const persisted = readPersistedSelections(organizationId);
    if (persisted) {
      setSelectionMode(persisted.selectionMode);
      setSelectedClusterId(persisted.selectedClusterId);
      setSelectedLocationIds(persisted.selectedLocationIds);
      setSelectedESimSelection(persisted.selectedESimSelection);
      setSelectedNetworkMccMnc(persisted.selectedNetworkMccMnc);
      setSelectedRat(persisted.selectedRat);
      setApn(persisted.apn || '');
    } else {
      setSelectionMode('individual');
      setSelectedClusterId(null);
      setSelectedLocationIds([]);
      setSelectedESimSelection(null);
      setSelectedNetworkMccMnc(null);
      setSelectedRat('auto');
      setApn('');
    }
    hasRestoredRef.current = organizationId;
  }, [organizationId]);

  // Persist selections whenever they change (after the initial restore for this org)
  useEffect(() => {
    if (!organizationId) return;
    if (hasRestoredRef.current !== organizationId) return;

    const payload: PersistedSelections = {
      selectionMode,
      selectedClusterId,
      selectedLocationIds,
      selectedESimSelection,
      selectedNetworkMccMnc,
      selectedRat,
      apn,
    };
    try {
      localStorage.setItem(storageKeyFor(organizationId), JSON.stringify(payload));
    } catch {
      // localStorage may be unavailable (private mode, quota) — selections simply won't persist.
    }
  }, [organizationId, selectionMode, selectedClusterId, selectedLocationIds, selectedESimSelection, selectedNetworkMccMnc, selectedRat, apn]);

  // Load cluster, eSIM group, and location details when selections change.
  // Also silently drops stale persisted selections that no longer resolve.
  useEffect(() => {
    async function loadResourceDetails() {
      if (!organizationId) return;

      try {
        // Load cluster details if cluster is selected; clear selection if not found
        if (selectedClusterId) {
          const cluster = await getCluster(selectedClusterId);
          if (cluster) {
            setSelectedClusterDetails(cluster);
          } else {
            setSelectedClusterDetails(null);
            setSelectedClusterId(null);
          }
        } else {
          setSelectedClusterDetails(null);
        }

        // Load details for whatever step 2 points at. Only a bare group id is
        // a group; `profile:<id>` is a specific SIM and `shared:<sentinel>` is
        // a pool token that resolves server-side (not a doc — never fetch it).
        // In both fetch paths, clear the selection only when the server
        // confirms the doc is gone (null). Don't wipe on transient errors —
        // that caused persisted selections to silently disappear.
        const esimSel = parseESimSelection(selectedESimSelection);
        if (esimSel.groupId && organizationId) {
          setSelectedESimProfileDetails(null);
          try {
            const group = await getGroup(esimSel.groupId, organizationId);
            if (group) {
              setSelectedESimGroupDetails(group);
            } else {
              setSelectedESimGroupDetails(null);
              setSelectedESimSelection(null);
            }
          } catch (err) {
            console.warn('[QuickTests] getGroup failed, keeping selection:', err);
          }
        } else if (isConcreteProfileSelection(selectedESimSelection) && esimSel.profileId && organizationId) {
          setSelectedESimGroupDetails(null);
          try {
            const profile = await getProfile(esimSel.profileId, organizationId);
            if (profile) {
              setSelectedESimProfileDetails(profile);
            } else {
              setSelectedESimProfileDetails(null);
              setSelectedESimSelection(null);
            }
          } catch (err) {
            console.warn('[QuickTests] getProfile failed, keeping selection:', err);
          }
        } else {
          setSelectedESimGroupDetails(null);
          setSelectedESimProfileDetails(null);
        }

        // Load all locations for name resolution (tenancy-filtered, #202)
        if (organizationId) {
          const locations = await getLocations();
          setAllLocations(filterLocationsByTenancy(locations, organizationId, isSuperAdmin));
        }
      } catch (error) {
        console.error('Error loading resource details:', error);
      }
    }

    loadResourceDetails();
  }, [selectedClusterId, selectedESimSelection, organizationId]);

  // Prune stale persisted location IDs once the canonical locations list loads
  useEffect(() => {
    if (allLocations.length === 0) return;
    if (selectedLocationIds.length === 0) return;
    const validIds = new Set(allLocations.map(l => l.id));
    const pruned = selectedLocationIds.filter(id => validIds.has(id));
    if (pruned.length !== selectedLocationIds.length) {
      setSelectedLocationIds(pruned);
    }
  }, [allLocations, selectedLocationIds]);

  // Check if resources are selected
  const hasResourcesSelected =
    (selectionMode === 'cluster' && selectedClusterId) ||
    (selectionMode === 'individual' && selectedLocationIds.length > 0);
  const hasESimSelected = selectedESimSelection !== null;
  // Step 2 holds one encoded value that can mean a group, one specific SIM, or
  // a shared pool. Decode it once here: the two profile-ish forms travel as
  // `eSimProfileId` (which the allocator resolves to — and for a resident SIM
  // pins to — the holder modem), the group form as `eSimGroupId`.
  const { groupId: groupIdForRun, profileId: profileIdForRun } =
    parseESimSelection(selectedESimSelection);
  // APN is an optional override since issue #161 — empty means "use the
  // eSIM's configured APN", so it never gates the test tiles.
  const canRunTests = hasResourcesSelected && hasESimSelected && !esimBlockedReason;

  // Test metadata
  const testMetadata: Record<QuickTestType, { label: string; description: string }> = {
    network: {
      label: 'Network Connection Test',
      description: 'Connect to the network and check registration, signal strength, and operator'
    },
    sms: {
      label: 'SMS Test',
      description: 'Connect to the network and send a test SMS'
    },
    data: {
      label: 'Data Connection Test',
      description: 'Activate a data session and verify IP connectivity'
    },
    data_performance: {
      label: 'Data Performance Test',
      description: 'Ping, HTTP timing, and throughput through the modem data plane'
    },
    iot_mqtt: {
      label: 'IoT (MQTT)',
      description: 'Verify LTE-M / NB-IoT attach and MQTT pub/sub round-trip through a managed broker'
    },
    iperf: {
      label: 'iperf3 Throughput',
      description: 'Measure TCP and UDP throughput (plus UDP jitter and packet loss) against an iperf3 server'
    },
    speedtest: {
      label: 'Speed Test',
      description: 'Download / upload / ping / jitter with a live gauge (LibreSpeed)'
    },
    voice_call: {
      label: 'Voice Call Check',
      description: 'Place a real call and check bearer (VoLTE / VoNR / CS), codec, setup time, and drop'
    },
    voice_agent: {
      label: 'Calling Card Check',
      description: 'AI agent dials a calling-card access number, enters the PIN, dials your destination, and confirms the call connects'
    },
    test_like_mobile: {
      label: 'Test Like A Mobile',
      description: 'Interactive phone simulator with dialer, messaging, and browser'
    }
  };

  /**
   * When a network type is forced, returns a short reason a given test can't
   * run — i.e. no modem at the selected location(s) can be both the forced RAT
   * and a modem the test needs. Mirrors the server's allocation gate so the
   * card is grayed out up-front instead of failing after the user clicks run.
   * Returns undefined (test stays enabled) when on Automatic, when the test has
   * no capability requirement, or while modem capabilities are still unknown.
   */
  const ratIncompatibleReason = (testType: QuickTestType): string | undefined => {
    const required = QUICK_TEST_ALLOCATION_CAPABILITIES[testType];
    if (!required || required.length === 0) return undefined;
    if (modemCaps === null) return undefined; // capabilities not loaded yet
    // A pinned SIM locks the run to one modem, so its fixed capabilities apply
    // even on Automatic (no other modem to fall back to). Otherwise only gate
    // when a network type is forced.
    const pinnedToModem = pinnedModemImei !== null;
    if (selectedRat === 'auto' && !pinnedToModem) return undefined;
    if (someModemSupportsTest(modemCaps, selectedRat, required)) return undefined;
    const capLabels = required.map(c => CAPABILITY_LABELS[c] || c);
    const capPhrase =
      capLabels.length <= 1
        ? capLabels.join('')
        : `${capLabels.slice(0, -1).join(', ')} or ${capLabels[capLabels.length - 1]}`;
    if (selectedRat === 'auto' && pinnedToModem) {
      return `The selected SIM's modem doesn't support this test (needs ${capPhrase})`;
    }
    return `Needs ${capPhrase} — not available with ${FORCED_RAT_LABELS[selectedRat]}`;
  };

  /**
   * Map test type to system flow ID (must match quickTestService.ts)
   */
  function getFlowIdForTestType(testType: QuickTestType): string {
    switch (testType) {
      case 'network': return 'sys_quick_network_test_flow';
      case 'sms': return 'sys_quick_sms_test_flow';
      case 'data': return 'sys_quick_data_test_flow';
      case 'data_performance': return 'sys_quick_data_performance_test_flow';
      case 'iot_mqtt': return 'sys_quick_iot_mqtt_test_flow';
      case 'iperf': return 'sys_quick_iperf_test_flow';
      case 'speedtest': return 'sys_quick_speedtest_test_flow';
      case 'voice_call': return 'sys_quick_voice_call_test_flow';
      case 'voice_agent': return 'sys_quick_voice_agent_test_flow';
      default: return '';
    }
  }

  /**
   * Map test type to system test configuration ID
   */
  function getConfigIdForTestType(testType: QuickTestType): string {
    switch (testType) {
      case 'network': return 'sys_config_network_test';
      case 'sms': return 'sys_config_sms_test';
      case 'data': return 'sys_config_data_test';
      case 'data_performance': return 'sys_config_data_test';
      case 'iot_mqtt': return 'sys_config_iot_mqtt_test';
      case 'iperf': return 'sys_config_iperf_test';
      case 'speedtest': return 'sys_config_speedtest_test';
      case 'voice_call': return 'sys_config_voice_call_test';
      case 'voice_agent': return 'sys_config_voice_agent_test';
      default: return '';
    }
  }

  const handleTestClick = async (testType: QuickTestType) => {
    if (!canRunTests) {
      toastError('Please select locations and an eSIM first');
      return;
    }

    if (!organizationId || !user) {
      toastError('Authentication error. Please log in again.');
      return;
    }

    // Special case: Test Like A Mobile - allocate resources and open modal
    if (testType === 'test_like_mobile') {
      try {
        const locationIds = selectionMode === 'cluster' && selectedClusterDetails?.items
          ? resolveClusterItems(selectedClusterDetails.items, allLocations)
          : selectedLocationIds;

        // A specific SIM (org profile or shared pool): skip group resolution —
        // pass the profile id directly and let /tlam/sessions place it (a
        // resident/shared SIM pins to the modem physically holding it).
        // Otherwise resolve the first available member of the selected group.
        const autoStartConfig = profileIdForRun
          ? { eSimProfileId: profileIdForRun, locationId: locationIds[0], deviceId: '', modemImei: '' }
          : await selectFirstAvailableResources(
              groupIdForRun!,
              locationIds,
              organizationId
            );

        if (autoStartConfig) {
          // Carry the wizard's Network selection into the TLAM session — the
          // backend will force-register the modem on this PLMN or hard-fail
          // the session. Without this the modem would roam onto whatever
          // network automatic selection picks, defeating the purpose of the
          // wizard's Network field.
          setTestLikeAMobileAutoStart({
            ...autoStartConfig,
            // Pass the selected group so /tlam/sessions resolves the right
            // member for the allocated modem (reuse-on-modem else install).
            // A specific SIM carries eSimProfileId only (no group).
            eSimGroupId: profileIdForRun ? undefined : (groupIdForRun || undefined),
            forcedNetworkMccMnc: selectedNetworkMccMnc || undefined,
            forcedRat: selectedRat !== 'auto' ? selectedRat : undefined,
            // Step-3 APN override → warms the data bearer at allocation time
            // and feeds PhoneBrowser. Empty = let the backend resolve the
            // eSIM's configured APN (profile → group → network); the session
            // response then carries the resolved value back to the modal.
            apn: (apn || '').trim() || undefined,
          });
          setShowTestLikeAMobile(true);
        } else {
          toastError('Failed to allocate resources for Test Like A Mobile');
        }
      } catch (error: any) {
        console.error('Error allocating resources for Test Like A Mobile:', error);
        toastError(error.message || 'Failed to allocate resources');
      }
      return;
    }

    // For queued tests we don't pre-check modem/eSIM availability — the
    // backend's QueueService allocator is the single source of truth and
    // will either dispatch or park the test with the real reason. Pre-flight
    // here just races the allocator and sometimes falsely blocks valid tests.
    try {
      // Load the system flow definition to check for required inputs
      const flowId = getFlowIdForTestType(testType);
      const flowDef = flowId ? await getFlow(flowId, organizationId) : null;
      // inputs field is stored on system flow docs but not in the Flow TypeScript type
      // Filter out inputs that are handled by the Quick Tests UI (not the dialog)
      // `apn` is now collected once in Step 3 (mandatory) and injected into
      // every flow below — so drop it from the per-test inputs dialog.
      const uiHandledInputs = new Set(['networkMccMnc', 'apn']);
      const flowInputs: FlowInputDefinition[] = (((flowDef as any)?.inputs || []) as FlowInputDefinition[])
        // Drop UI-handled inputs (rendered elsewhere) and platform-managed
        // hidden inputs (e.g. LibreSpeed server_host/token — always our server,
        // resolved server-side from the preset default).
        .filter(input => !uiHandledInputs.has(input.name) && !input.hidden);
      const hasInputs = flowInputs.length > 0;

      // Set pending test metadata
      setPendingTest({
        type: testType,
        label: testMetadata[testType].label,
        description: testMetadata[testType].description,
      });

      if (hasInputs) {
        // Show inputs dialog first, then confirmation
        setPendingFlowInputDefs(flowInputs);
        setPendingInputs({});
        setShowInputsDialog(true);
      } else {
        // No inputs needed, go straight to confirmation
        setPendingInputs({});
        setShowConfirmation(true);
      }
    } catch (error: any) {
      console.error('Error preparing test:', error);
      toastError(error.message || 'Failed to prepare test');
    }
  };

  /**
   * Handle flow inputs submission — move to confirmation dialog
   */
  const handleInputsSubmit = (values: Record<string, any>) => {
    setPendingInputs(values);
    setShowInputsDialog(false);
    setShowConfirmation(true);
  };

  const handleConfirmExecution = async (scope: 'once' | 'cluster') => {
    if (!pendingTest || !organizationId || !user) return;

    setIsExecuting(true);
    setShowConfirmation(false);

    try {
      // Resolve location IDs: from cluster items or direct selection
      const locationIds = selectionMode === 'cluster' && selectedClusterDetails?.items
        ? resolveClusterItems(selectedClusterDetails.items, allLocations)
        : selectedLocationIds;

      if (locationIds.length === 0) {
        throw new Error('No locations selected');
      }

      // Load the system test configuration
      const configId = getConfigIdForTestType(pendingTest.type);
      const systemConfig = await getTestConfiguration(configId, organizationId);
      if (!systemConfig) {
        throw new Error(`System test configuration not found: ${configId}`);
      }

      // Load the flow definition — use the test type mapping (not the config's flowId)
      const flowId = getFlowIdForTestType(pendingTest.type) || systemConfig.flowId || '';
      const flowDef = await getFlow(flowId, organizationId);
      if (!flowDef) {
        throw new Error(`System flow not found: ${flowId}`);
      }

      // "Run Once" runs at the first selected location only; "Run Across All"
      // fans out one independent test per selected location.
      const targetLocationIds = scope === 'once' ? [locationIds[0]] : locationIds;

      // Group test: send the eSIM GROUP id and let the backend resolve a member
      // PER ALLOCATED MODEM — reuse one already installed there (incl. consumed
      // single-use members, status 'downloaded'), else install an available
      // one. This replaces client-side resolution to a single 'available'
      // profile, which excluded consumed single-use members and dead-ended once
      // each code had been used once. No empty-group pre-check here: the backend
      // reports a clear reason if the group has no usable member.

      // Shared flow inputs. Keep `networkMccMnc` flow input for any system flow
      // that still reads it (TLAM today); the executionContext.forcedNetworkMccMnc
      // below wires the same value into the modem-level network lock for the rest.
      const flowInputs = { ...pendingInputs };
      if (selectedNetworkMccMnc) {
        flowInputs.networkMccMnc = selectedNetworkMccMnc;
      }
      // Inject the Step-3 APN override only when the user typed one. Empty →
      // the key is OMITTED so the backend resolves the eSIM's configured APN
      // (profile → group → network default) at submit/dispatch, falling back
      // to the flow's declared default (`internet`). Sending a value here
      // would register as an explicit override and defeat that resolution
      // (issue #161).
      const apnOverride = (apn || '').trim();
      if (apnOverride) {
        flowInputs.apn = apnOverride;
      }

      // Force-RAT / Force-PLMN ride in executionContext for every run.
      const executionContext = {
        ...(selectedRat !== 'auto' ? { forcedRat: selectedRat } : {}),
        ...(selectedNetworkMccMnc ? { forcedNetworkMccMnc: selectedNetworkMccMnc } : {}),
      };

      // Fire one test per target location, each with its own TestRun + eSIM so
      // it shows as an independent row on /tests. Await each submit so pre-flight
      // failures (NO_CAPABLE_MODEM, no free modem, etc.) surface per location —
      // the backend returns 202 in <500ms when queued, so awaiting is cheap.
      const launchAtLocation = async (
        locId: string,
        _index: number
      ): Promise<{ testRunId: string; locationId: string }> => {
        const start = async (allowEsimRelocation: boolean) => {
          // The snapshot records what was picked (eSIM, network type,
          // network, APN override) so Test Run Details can show it and a
          // retry repeats it. The move approval rides along the same way.
          const runtimeConfig: TestConfiguration = {
            ...systemConfig,
            locationRequirements: [{ id: 'loc-0', locationId: locId, modemCount: 1 }],
            esim: {
              ...(profileIdForRun
                ? { mode: 'single' as const, profileId: profileIdForRun }
                : { mode: 'batch' as const, batch: { type: 'group' as const, groupId: groupIdForRun! } }),
              ...(allowEsimRelocation ? { allowRelocation: true } : {}),
            },
            ...executionContext,
            // Display only: the network option text as picked, e.g. "AT&T (310/410)".
            ...(selectedNetworkMccMnc && selectedNetworkLabel ? ({ forcedNetworkLabel: selectedNetworkLabel } as any) : {}),
            ...(apnOverride ? { inputs: { ...(systemConfig.inputs || {}), apn: apnOverride } } : {}),
          };

          const testRun = await createTestRun(organizationId, user.uid, {
            flowId,
            flowName: systemConfig.flowName || systemConfig.name,
            flowType: (systemConfig.flowType || 'scenario') as any,
            flowVersion: systemConfig.flowVersion || 1,
            testConfigurationId: configId,
            configuration: runtimeConfig,
          });

          await executeFlow(
            flowDef,
            flowInputs,
            user.uid,
            organizationId,
            {
              ...(profileIdForRun
                ? { eSimProfileId: profileIdForRun }
                : { eSimGroupId: groupIdForRun! }),
              ...executionContext,
              ...(allowEsimRelocation ? { allowEsimRelocation: true } : {}),
            },
            locId,
            false, // useEmulator
            testRun.id // testRunId — backend links & syncs the test_run
          );

          return { testRunId: testRun.id, locationId: locId };
        };
        try {
          try {
            return await start(false);
          } catch (err: any) {
            // #375: refused only for want of consent to move a limited-download
            // eSIM — ask, and start an approved run (the refused one stays in history).
            if (!isEsimMoveApprovalError(err)) throw err;
            if (await confirmMove(err.message)) return await start(true);
            throw Object.assign(new Error(ESIM_MOVE_DECLINED), { declined: true });
          }
        } catch (err: any) {
          // Tag the failure with its location so the summary toast can name it.
          const wrapped: any = new Error(err?.message || 'Failed to start test');
          wrapped.locationId = locId;
          wrapped.declined = err?.declined === true;
          throw wrapped;
        }
      };

      const results = await Promise.allSettled(
        targetLocationIds.map((locId, i) => launchAtLocation(locId, i))
      );

      const succeeded = results.filter(
        (r): r is PromiseFulfilledResult<{ testRunId: string; locationId: string }> =>
          r.status === 'fulfilled'
      );
      const failed = results.filter(
        (r): r is PromiseRejectedResult => r.status === 'rejected'
      );

      if (succeeded.length === 0) {
        console.error('Flow execution submit error(s):', failed.map(f => f.reason));
        if (failed.every((f) => f.reason?.declined)) {
          toastInfo(ESIM_MOVE_DECLINED);
          return;
        }
        toastError(failed[0]?.reason?.message || 'Failed to start test');
        return; // No navigate — stay on the page so the user can adjust
      }

      if (failed.length > 0) {
        const locName = (id: string) => allLocations.find(l => l.id === id)?.name || id;
        const failedNames = failed
          .map(f => (f.reason?.locationId ? locName(f.reason.locationId) : null))
          .filter(Boolean);
        toastError(
          `Started ${succeeded.length} test${succeeded.length !== 1 ? 's' : ''}, ` +
            `${failed.length} failed${failedNames.length ? ` (${failedNames.join(', ')})` : ''}: ` +
            (failed[0]?.reason?.message || 'unknown error')
        );
      } else {
        toastSuccess(
          succeeded.length === 1
            ? 'Test started successfully!'
            : `Started ${succeeded.length} tests across ${succeeded.length} locations`
        );
      }

      // Link a single run directly; for a fan-out just open the running list.
      navigate(
        succeeded.length === 1
          ? `/tests?tab=running&runId=${succeeded[0].value.testRunId}`
          : `/tests?tab=running`
      );
    } catch (error: any) {
      console.error('Error executing test:', error);
      toastError(error.message || 'Failed to execute test');
    } finally {
      setIsExecuting(false);
      setPendingTest(null);
    }
  };

  return (
    <AppShell>
      {esimMoveDialog}
      <div className="flex flex-col gap-4 sm:gap-5">
        {/* Page Header */}
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">Quick Tests</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Pick where to test, optionally constrain the network and technology, then run.
          </p>
        </div>

        <ResourceSelectors
          selectionMode={selectionMode}
          onSelectionModeChange={setSelectionMode}
          selectedClusterId={selectedClusterId}
          onClusterChange={setSelectedClusterId}
          selectedLocationIds={selectedLocationIds}
          onLocationIdsChange={setSelectedLocationIds}
          selectedESimSelection={selectedESimSelection}
          onESimSelectionChange={setSelectedESimSelection}
          selectedNetworkMccMnc={selectedNetworkMccMnc}
          onNetworkChange={setSelectedNetworkMccMnc}
          selectedRat={selectedRat}
          onRatChange={setSelectedRat}
          apn={apn}
          onApnChange={setApn}
          onModemCapsChange={setModemCaps}
          onESimBlockedChange={setEsimBlockedReason}
          onPinnedModemChange={setPinnedModemImei}
          onNetworkLabelChange={setSelectedNetworkLabel}
        />

        {/* Step 4: Quick Test Buttons */}
        <div>
          <div className="flex items-center gap-3 mb-3">
            <div className={`flex items-center justify-center w-7 h-7 rounded-full text-sm font-bold ${canRunTests ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
              4
            </div>
            <h2 className="text-base font-semibold text-foreground">Choose a Test</h2>
            {/* Distinguish "not configured yet" from "configured, but this SIM
                can't run here" — the latter is actionable and otherwise reads
                as an unexplained grey-out. */}
            {esimBlockedReason ? (
              <span className="text-sm text-destructive">— {esimBlockedReason}</span>
            ) : !canRunTests ? (
              <span className="text-sm text-muted-foreground">
                — Complete steps 1 and 2 first
              </span>
            ) : null}
          </div>
          <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 ${!canRunTests ? 'opacity-50 pointer-events-none' : ''}`}>
            <QuickTestButton
              type="network"
              icon={Signal}
              label="Network Connection Test"
              description="Check network registration, operator, technology, and signal strength"
              disabled={!canRunTests}
              incompatibleReason={ratIncompatibleReason('network')}
              onClick={() => handleTestClick('network')}
            />
            <QuickTestButton
              type="sms"
              icon={MessageSquare}
              label="SMS Test"
              description="Send a test SMS and verify delivery status"
              disabled={!canRunTests}
              incompatibleReason={ratIncompatibleReason('sms')}
              onClick={() => handleTestClick('sms')}
            />
            <QuickTestButton
              type="data"
              icon={Database}
              label="Data Connection Test"
              description="Activate data session and ping a target host to verify end-to-end connectivity"
              disabled={!canRunTests}
              incompatibleReason={ratIncompatibleReason('data')}
              onClick={() => handleTestClick('data')}
            />
            <QuickTestButton
              type="data_performance"
              icon={Activity}
              label="Data Performance Test"
              description="Ping latency, HTTP timing, and download throughput through the modem data plane"
              disabled={!canRunTests}
              incompatibleReason={ratIncompatibleReason('data_performance')}
              onClick={() => handleTestClick('data_performance')}
            />
            <QuickTestButton
              type="iot_mqtt"
              icon={Radio}
              label="IoT (MQTT)"
              description="Verify LTE-M / NB-IoT attach and MQTT pub/sub round-trip through a managed broker"
              disabled={!canRunTests}
              incompatibleReason={ratIncompatibleReason('iot_mqtt')}
              onClick={() => handleTestClick('iot_mqtt')}
            />
            <QuickTestButton
              type="iperf"
              icon={Gauge}
              label="iperf3 Throughput"
              description="Measure TCP and UDP throughput, plus UDP jitter and packet loss, against an iperf3 server"
              disabled={!canRunTests}
              incompatibleReason={ratIncompatibleReason('iperf')}
              onClick={() => handleTestClick('iperf')}
            />
            <QuickTestButton
              type="speedtest"
              icon={Zap}
              label="Speed Test"
              description="Download / upload / ping / jitter with a live gauge (LibreSpeed)"
              disabled={!canRunTests}
              incompatibleReason={ratIncompatibleReason('speedtest')}
              onClick={() => handleTestClick('speedtest')}
            />
            <QuickTestButton
              type="voice_call"
              icon={Phone}
              label="Voice Call Check"
              description="Place a real call and check bearer (VoLTE / VoNR / CS), codec, setup time, and drop"
              disabled={!canRunTests}
              incompatibleReason={ratIncompatibleReason('voice_call')}
              onClick={() => handleTestClick('voice_call')}
            />
            <QuickTestButton
              type="voice_agent"
              icon={Bot}
              label="Calling Card Check"
              description="AI agent dials a calling-card access number, enters the PIN, dials your destination, and confirms the call connects"
              disabled={!canRunTests}
              incompatibleReason={ratIncompatibleReason('voice_agent')}
              onClick={() => handleTestClick('voice_agent')}
            />
            <QuickTestButton
              type="test_like_mobile"
              icon={Smartphone}
              label="Test Like A Mobile"
              description="Interactive phone simulator with dialer, messaging, and browser"
              disabled={!canRunTests}
              incompatibleReason={ratIncompatibleReason('test_like_mobile')}
              featured
              onClick={() => handleTestClick('test_like_mobile')}
            />
          </div>
        </div>
      </div>

      {/* Flow Inputs Dialog (shown before confirmation when flow has inputs) */}
      {pendingTest && (
        <FlowInputsDialog
          isOpen={showInputsDialog}
          onClose={() => {
            setShowInputsDialog(false);
            setPendingTest(null);
          }}
          testLabel={pendingTest.label}
          inputs={pendingFlowInputDefs}
          onSubmit={handleInputsSubmit}
          storageKey={getFlowIdForTestType(pendingTest.type)}
        />
      )}

      {/* Execution Confirmation Dialog */}
      {pendingTest && (
        <ExecutionConfirmationDialog
          isOpen={showConfirmation}
          onClose={() => {
            setShowConfirmation(false);
            setPendingTest(null);
          }}
          testType={pendingTest.type}
          testLabel={pendingTest.label}
          testDescription={pendingTest.description}
          selectionMode={selectionMode}
          clusterName={selectedClusterDetails?.name}
          locationNames={(
            selectionMode === 'cluster' && selectedClusterDetails?.items
              ? resolveClusterItems(selectedClusterDetails.items, allLocations)
              : selectedLocationIds
          ).map(id => {
            const location = allLocations.find(loc => loc.id === id);
            return location?.name || id;
          })}
          eSimGroupName={selectedESimGroupDetails?.name}
          eSimProfileCount={selectedESimGroupDetails?.profileCount || 0}
          eSimProfileName={
            selectedESimProfileDetails
              ? selectedESimProfileDetails.name || selectedESimProfileDetails.iccid
              : undefined
          }
          eSimProfileDetail={
            selectedESimProfileDetails
              ? [
                  selectedESimProfileDetails.groupName || 'No group',
                  selectedESimProfileDetails.networkName,
                  selectedESimProfileDetails.iccid,
                ]
                  .filter(Boolean)
                  .join(' · ')
              : undefined
          }
          onConfirm={handleConfirmExecution}
        />
      )}

      {/* Test Like A Mobile Modal */}
      <TestLikeAMobileModal
        isOpen={showTestLikeAMobile}
        onClose={() => {
          setShowTestLikeAMobile(false);
          setTestLikeAMobileAutoStart(undefined);
        }}
        autoStart={testLikeAMobileAutoStart}
      />
    </AppShell>
  );
}

export default QuickTestsPage;
